import { useState } from "react";
import { Link } from "react-router-dom";
import { useConfirmEmailChange } from "../../api/hooks";
import { useAuth } from "../../auth/AuthProvider";
import { Card, MutationError, PageHeader, btnPrimary } from "../../components/ui";

/** The token rides in the fragment, which no browser sends to a server; it is read once and taken out of the address bar. */
function takeToken(): string {
  const token = new URLSearchParams(window.location.hash.slice(1)).get("t") ?? "";
  if (token) window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
  return token;
}

/**
 * #182: where the confirmation email's link lands. Opening the page changes
 * nothing; the button does (a GET never mutates). On success every session
 * has been signed out server-side, so the next step is a sign-in with the
 * new address.
 */
export function ConfirmEmailPage() {
  const { me, signOut } = useAuth();
  const [token] = useState(takeToken);
  const confirm = useConfirmEmailChange();

  return (
    <>
      <PageHeader title="Confirm your new email address" />
      <Card>
        {confirm.isSuccess ? (
          <div role="status" className="space-y-3 text-sm">
            <p>Your email is now <span className="break-words font-medium">{confirm.data.email}</span>.</p>
            <p>Every session was signed out, this one included. Sign in again with your new address.</p>
            <button type="button" className={btnPrimary} onClick={() => void signOut()}>Sign in with the new address</button>
          </div>
        ) : !token ? (
          <p className="text-sm">This link is incomplete. Open it again from the email, or ask for a new one on <Link className="text-accent underline" to="/account#email">Account</Link>.</p>
        ) : (
          <div className="space-y-3 text-sm">
            <p>
              {me?.pendingEmail ? <>Make <span className="break-words font-medium">{me.pendingEmail.address}</span> the email on your account?</> : "Make this address the email on your account?"} You will then sign in with it, and {me?.email ?? "your current address"} stops working.
            </p>
            <button type="button" className={btnPrimary} disabled={confirm.isPending} onClick={() => confirm.mutate(token)}>
              {confirm.isPending ? "Confirming…" : "Confirm new email address"}
            </button>
            <MutationError of={confirm} />
          </div>
        )}
      </Card>
    </>
  );
}
